import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Compiler,
  Component,
  ComponentRef,
  Injector,
  NgModule,
  NgZone,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { DirectiveSuperclass } from '@sama/ng-core';
import { filter, switchMap, take } from 'rxjs/operators';
import { delayExecution } from '../../shared/misc-utilities/promise-utils';
import { H3mlHelper } from './h3ml-helper';
import { H3mlModule } from './h3ml.module';
import { H3mlStore } from './state/h3ml-store';
import { transformLayout } from './transform-layout';

const h3mlStyles = `
  table.spaced-columns td, table.spaced-columns th {
 vertical-align: top;
}
 table.spaced-columns td:not(:first-child), table.spaced-columns th:not(:first-child) {
 padding-left: 30px;
}
 table.spaced-columns td:not(:last-child), table.spaced-columns th:not(:last-child) {
 padding-right: 30px;
}
`;

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- not sure why it won't work with on push
@Component({
  selector: 'h3-h3ml-container',
  template: '<ng-container #container></ng-container>',
  standalone: false,
})
export class H3mlContainerComponent
  extends DirectiveSuperclass
  implements AfterViewInit
{
  @ViewChild('container', { read: ViewContainerRef })
  private containerRef!: ViewContainerRef;
  private dynamicComponentRef?: ComponentRef<any>;

  constructor(
    private compiler: Compiler,
    private h3mlHelper: H3mlHelper,
    private ngZone: NgZone,
    private store: H3mlStore,
    injector: Injector,
  ) {
    super(injector);
  }

  ngAfterViewInit() {
    // wait for a task to exist, then start watching for layout changes
    this.subscribeTo(
      this.store('task').$.pipe(
        filter((task) => !!task),
        take(1),
        switchMap(() => this.store('layout').$),
      ),
      this.setLayout,
    );
  }

  private setLayout(layout: string) {
    if (this.dynamicComponentRef) {
      this.dynamicComponentRef.destroy();
      this.dynamicComponentRef = undefined;
    }

    if (layout) {
      // delay in case this is called during `ngAfterViewInit()`, which causes
      // an Angular error. (happens in the nested output popup)
      void delayExecution(async () => {
        this.dynamicComponentRef = await this.addComponent(
          transformLayout(layout),
        );

        // Angular does some asynchronous things while initializing the layout
        // (like running change detection). `onStable` ensures those have all
        // finished.
        this.subscribeTo(this.ngZone.onStable.pipe(take(1)), () => {
          this.store.layoutComplete$.next();
        });
      });
    }
  }

  // https://stackoverflow.com/a/39507831/1836506
  private async addComponent(template: string) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- no workaround
    const self = this;

    // This is very unusual code, creating components and modules dynamically.
    // These very small classes are only used here, so to make the code easier
    // to understand we made an exception to this rule.
    const templateComponent = Component({
      template,
      styles: [h3mlStyles],
      standalone: false,
    })(
      class TemplateComponent {
        constructor() {
          self.h3mlHelper.install(this);
        }
      },
    );

    const templateModule = NgModule({
      imports: [CommonModule, H3mlModule, MatTabsModule],
      declarations: [templateComponent],
    })(class TemplateModule {});

    const module =
      await this.compiler.compileModuleAndAllComponentsAsync(templateModule);
    const factory = module.componentFactories.find(
      (comp) => comp.componentType === templateComponent,
    )!;
    return this.containerRef.createComponent(factory);
  }
}
